import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { QueryClientProvider } from '@tanstack/react-query';

import Login from '../../features/auth/Login/Login';
import Logout from '../../features/auth/Logout/Logout';
import Unauthorized from '../../features/auth/Unauthorized/Unauthorized';
import AppShell from '../../app/AppShell/AppShell';
import { cedarModules } from '../../../modules';
import ProtectedRoute from '../../features/auth/ProtectedRoute/ProtectedRoute';
import DashboardPage from '../../../pages/DashboardPage';
import NotFoundPage from '../../../pages/NotFoundPage';
import queryClient from '../../common/reactQueryClient';
import DevQueryTools from '../../common/DevQueryTools';
import ExplorerLabAccessGate from '../../../modules/data/components/ExplorerLabAccessGate';

import './App.css';
import { useMsal } from '@azure/msal-react';
import { Suspense, useEffect, useState } from 'react';
import Toast from '../../common/Toast';
import type { CedarModuleConfig } from '../../../modules';

type AppRoute = {
    path: string;
    element: React.ReactElement;
    title: string;
    isProtected: boolean;
};

const toToolRoutes = (modules: CedarModuleConfig[]): AppRoute[] => {
    return modules.flatMap((module) =>
        (module.tools ?? [])
            .filter((tool) => tool.enabled !== false && !tool.shouldHide)
            .flatMap((tool) => {
                const title = tool.title || `${module.name ?? module.domain.title} - ${tool.name}`;
                const aliases = (tool.aliases ?? []).map((aliasPath) => ({
                    path: aliasPath,
                    element: tool.element,
                    title,
                    isProtected: true,
                }));

                return [
                    {
                        path: tool.path,
                        element: tool.element,
                        title,
                        isProtected: true,
                    },
                    ...aliases,
                ];
            })
    );
};

export const buildAppRoutes = (modules: CedarModuleConfig[]): AppRoute[] => {
    const publicRoutes: AppRoute[] = [
        { path: "/", element: <DashboardPage />, title: "Dashboard", isProtected: true },
        { path: "/dashboard", element: <DashboardPage />, title: "Dashboard", isProtected: true },
        { path: "/login", element: <Login />, title: "Login", isProtected: false },
        { path: "/logout", element: <Logout />, title: "Logout", isProtected: false },
        { path: "/unauthorized", element: <Unauthorized />, title: "Unauthorized", isProtected: false },
    ];

    return [...publicRoutes, ...toToolRoutes(modules)];
};

const routes: AppRoute[] = buildAppRoutes(cedarModules);

const App = () => {
    const { instance, accounts } = useMsal();
    const location = useLocation();
    const navigate = useNavigate();
    const [redirecting, setRedirecting] = useState(false);

    useEffect(() => {
        // Set active account if not already set
        if (!instance.getActiveAccount() && accounts.length > 0) {
            const firstAccount = accounts[0];
            if (firstAccount) {
                instance.setActiveAccount(firstAccount);
            }
        }
        // Only redirect if user is authenticated and a redirect is pending
        const account = instance.getActiveAccount();
        const redirectUrl = sessionStorage.getItem('postLoginRedirect');
        if (account && redirectUrl && location.pathname !== redirectUrl) {
            setRedirecting(true);
            sessionStorage.removeItem('postLoginRedirect');
            navigate(redirectUrl, { replace: true });
        } else {
            setRedirecting(false);
        }
    }, [instance, accounts, navigate, location]);

    useEffect(() => {
        if (!instance.getActiveAccount() && accounts.length > 0) {
            const firstAccount = accounts[0];
            if (firstAccount) {
                instance.setActiveAccount(firstAccount);
            }
        }
    }, [instance, accounts]);

    // Move this AFTER all hooks
    if (redirecting) {
        // Can show a spinner here in the future if we desire - not needed now as app is performant enough
        return null;
    }

    // Find the active route to set the title
    const currentRoute = routes.find((route) => route.path === location.pathname);
    const pageTitle = currentRoute?.title ? `${currentRoute.title} | Cedar` : "Cedar";

    return (
        <QueryClientProvider client={queryClient}>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <DevQueryTools />
            <Toast />
            <Routes>
                {/* Public routes */}
                {routes.filter(x => !x.isProtected).map(({ path, element }) => (
                    <Route key={path} path={path} element={element} />
                ))}
                {/* Protected routes */}
                {routes.filter(x => x.isProtected).map(({ path, element }) => (
                    <Route
                        key={path}
                        path={path}
                        element={
                            <ProtectedRoute>
                                {path === '/data/explorer-lab' ? (
                                    <ExplorerLabAccessGate>
                                        <AppShell>
                                            <Suspense fallback={null}>
                                                {element}
                                            </Suspense>
                                        </AppShell>
                                    </ExplorerLabAccessGate>
                                ) : (
                                    <AppShell>
                                        <Suspense fallback={null}>
                                            {element}
                                        </Suspense>
                                    </AppShell>
                                )}
                            </ProtectedRoute>
                        }
                    />
                ))}
                {/* Catch-all route for 404 - must be last */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </QueryClientProvider>
    );
};

export default App;
